import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, Plus, User, Mail, Phone, ArrowRight, Globe, Inbox, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stages = [
  { id: 'applied', label: 'Applied', color: 'bg-muted' },
  { id: 'screening', label: 'Screening', color: 'bg-blue-500/20' },
  { id: 'interview_1', label: 'Interview 1', color: 'bg-purple-500/20' },
  { id: 'interview_2', label: 'Interview 2', color: 'bg-indigo-500/20' },
  { id: 'offer', label: 'Offer', color: 'bg-yellow-500/20' },
  { id: 'hired', label: 'Hired', color: 'bg-green-500/20' },
  { id: 'rejected', label: 'Rejected', color: 'bg-red-500/20' },
];

const ATSPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCandidate, setNewCandidate] = useState({ name: '', email: '', phone: '', position: '', source: '' });
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/candidates`, { withCredentials: true });
      setCandidates(response.data);
    } catch (error) {
      console.error('Fetch candidates error:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCandidate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/candidates`, newCandidate, { withCredentials: true });
      toast.success('Candidate added');
      setIsAddDialogOpen(false);
      setNewCandidate({ name: '', email: '', phone: '', position: '', source: '' });
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add candidate');
    }
  };

  const handleStageUpdate = async (candidateId, stage) => {
    try {
      await axios.put(`${API_URL}/api/candidates/${candidateId}/stage`, { stage }, { withCredentials: true });
      toast.success('Stage updated');
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const getStageBadge = (stage) => {
    const s = stages.find(st => st.id === stage);
    return <Badge className={`${s?.color || 'bg-muted'} border-0`}>{s?.label || stage}</Badge>;
  };

  // Group by stage for Kanban view
  const candidatesByStage = stages.reduce((acc, stage) => {
    acc[stage.id] = candidates.filter(c => c.stage === stage.id);
    return acc;
  }, {});

  // Distinct sources for filter dropdown
  const availableSources = Array.from(new Set(candidates.map(c => c.source).filter(Boolean))).sort();
  const filteredCandidates = sourceFilter === 'all'
    ? candidates
    : candidates.filter(c => (c.source || 'direct') === sourceFilter);
  const careersInboxCount = candidates.filter(c => c.source === 'careers-website').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ats-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-primary" />
            Applicant Tracking
          </h1>
          <p className="text-muted-foreground">Manage candidates through the hiring pipeline</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-candidate-btn"><Plus className="w-4 h-4" />Add Candidate</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Candidate</DialogTitle></DialogHeader>
            <form onSubmit={handleAddCandidate} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={newCandidate.name} onChange={(e) => setNewCandidate({ ...newCandidate, name: e.target.value })} required data-testid="candidate-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={newCandidate.email} onChange={(e) => setNewCandidate({ ...newCandidate, email: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={newCandidate.phone} onChange={(e) => setNewCandidate({ ...newCandidate, phone: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Position</Label>
                <Input value={newCandidate.position} onChange={(e) => setNewCandidate({ ...newCandidate, position: e.target.value })} placeholder="e.g., ML Engineer" required />
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select value={newCandidate.source} onValueChange={(v) => setNewCandidate({ ...newCandidate, source: v })}>
                  <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="job_board">Job Board</SelectItem>
                    <SelectItem value="direct">Direct Application</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="submit-candidate">Add</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {stages.map((stage) => (
          <Card key={stage.id} className={stage.color}>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">{stage.label}</p>
              <p className="text-xl font-bold">{candidatesByStage[stage.id]?.length || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Candidates List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              All Candidates
              {careersInboxCount > 0 && (
                <button
                  onClick={() => setSourceFilter(sourceFilter === 'careers-website' ? 'all' : 'careers-website')}
                  className={`ml-2 inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-0.5 border transition-colors ${
                    sourceFilter === 'careers-website'
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100'
                  }`}
                  data-testid="careers-inbox-chip"
                >
                  <Inbox className="w-3 h-3" />
                  {careersInboxCount} from wisewit.ai/careers
                </button>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground shrink-0">Filter by source:</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-[200px] h-8" data-testid="source-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources ({candidates.length})</SelectItem>
                  {availableSources.map(src => (
                    <SelectItem key={src} value={src}>
                      {src} ({candidates.filter(c => c.source === src).length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Resume</th>
                  <th>Stage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.map((candidate) => (
                  <tr key={candidate.id} data-testid={`candidate-row-${candidate.id}`}>
                    <td className="font-medium">{candidate.name}</td>
                    <td>{candidate.position}</td>
                    <td className="text-muted-foreground">{candidate.email}</td>
                    <td>
                      {candidate.source === 'careers-website' ? (
                        <Badge className="gap-1 bg-emerald-100 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800" variant="outline">
                          <Globe className="w-3 h-3" /> careers-website
                        </Badge>
                      ) : (
                        <Badge variant="outline">{candidate.source || 'Direct'}</Badge>
                      )}
                    </td>
                    <td>
                      {candidate.resume_url ? (
                        <a
                          href={`${API_URL}/api/uploads/${candidate.resume_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
                          data-testid={`resume-link-${candidate.id}`}
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td>{getStageBadge(candidate.stage)}</td>
                    <td>
                      <Select value={candidate.stage} onValueChange={(v) => handleStageUpdate(candidate.id, v)}>
                        <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {stages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredCandidates.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              {sourceFilter === 'all' ? 'No candidates in the pipeline' : `No candidates with source "${sourceFilter}"`}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ATSPage;
